import React, { Suspense, lazy, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MarkdownStreamingContext } from './CodeBlock';
import ResponsiveTable from './ResponsiveTable';
import { chartSpecToRows, parseChartSpec } from '../../utils/chatChartSpec';
import { trackChatUxEvent } from '../../utils/eventTracker';
import './ChatChart.css';

const ChatChartCanvas = lazy(() => import('./ChatChartCanvas'));

const MOBILE_QUERY = '(max-width: 640px)';

const useIsMobile = () => {
  const getMatch = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(MOBILE_QUERY).matches;
  const [mobile, setMobile] = useState(getMatch);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMobile(media.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);
  return mobile;
};

// 항목 이름이 길거나 많으면 가로 막대로 — 세로 막대 아래 글자가 겹치거나 잘리지 않게 한다.
const shouldUseHorizontal = (spec) => spec.type === 'bar'
  && (spec.labels.length > 8 || spec.labels.some((label) => Array.from(label).length > 6));

const chartHeight = (spec, horizontal, mobile) => {
  if (!horizontal) return mobile ? 220 : 260;
  const perLabel = 14 + 20 * spec.series.length;
  return Math.min(Math.max(spec.labels.length * perLabel + 48, 180), 640);
};

const isChatPage = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/chat');

const ChartTable = ({ spec }) => {
  const { header, rows } = chartSpecToRows(spec);
  return (
    <ResponsiveTable>
      <thead>
        <tr>{header.map((cell) => <th key={cell} scope="col">{cell}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
        ))}
      </tbody>
    </ResponsiveTable>
  );
};

// 차트 파일을 못 불러오거나(배포 직후 옛 화면) 그리다 실패하면 답변 전체가 아니라 이 자리만 표로 바꾼다.
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    if (isChatPage()) trackChatUxEvent('chart_invalid', { errorCode: error?.name || 'render_failed' });
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const ChatChart = ({ code }) => {
  const streaming = useContext(MarkdownStreamingContext);
  const parsed = useMemo(() => parseChartSpec(code), [code]);
  const mobile = useIsMobile();
  const [showTable, setShowTable] = useState(false);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (streaming || trackedRef.current || !isChatPage()) return;
    trackedRef.current = true;
    if (parsed.ok) {
      trackChatUxEvent('chart_shown', { sourceCount: parsed.spec.labels.length, stage: parsed.spec.type });
    } else {
      trackChatUxEvent('chart_invalid', { errorCode: parsed.error });
    }
  }, [parsed, streaming]);

  if (!parsed.ok) {
    if (streaming) {
      return <div aria-hidden="true" className="wq-chart wq-chart--pending"><div className="wq-chart__skeleton" /></div>;
    }
    return <p className="wq-chart-invalid">차트 데이터를 읽지 못해 차트를 표시하지 않았어요.</p>;
  }

  const { spec } = parsed;
  const horizontal = shouldUseHorizontal(spec);
  const height = chartHeight(spec, horizontal, mobile);
  const kind = spec.type === 'line' ? '꺾은선' : '막대';

  const toggle = () => {
    setShowTable((prev) => {
      if (!prev && isChatPage()) trackChatUxEvent('chart_table_opened', { sourceCount: spec.labels.length, stage: spec.type });
      return !prev;
    });
  };

  return (
    <figure className="wq-chart">
      <figcaption className="wq-chart__head">
        <span className="wq-chart__title">{spec.title}</span>
        {spec.unit && <span className="wq-chart__unit">단위: {spec.unit}</span>}
      </figcaption>
      {showTable ? (
        <ChartTable spec={spec} />
      ) : (
        <div
          aria-label={`${spec.title}, ${kind} 차트, 항목 ${spec.labels.length}개. 표로 보기 버튼으로 숫자를 확인할 수 있어요.`}
          className="wq-chart__canvas"
          role="img"
          style={{ height }}
        >
          <ChartErrorBoundary fallback={<ChartTable spec={spec} />}>
            <Suspense fallback={<div className="wq-chart__skeleton" />}>
              <ChatChartCanvas height={height} horizontal={horizontal} spec={spec} />
            </Suspense>
          </ChartErrorBoundary>
        </div>
      )}
      <div className="wq-chart__foot">
        {spec.source ? <span className="wq-chart__source">출처: {spec.source}</span> : <span />}
        <button aria-pressed={showTable} className="wq-chart__toggle" onClick={toggle} type="button">
          {showTable ? '차트로 보기' : '표로 보기'}
        </button>
      </div>
    </figure>
  );
};

export default ChatChart;
